import { LitElement, html, css } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';

import { ListUser } from '../../types/types';
import { getQueryParamURL, getSearchParam } from '../../util/list_util';
import '../../elements/langhout-button/langhout-button';
import {
  authenticatedAPICall,
  isSuccessfulAPIResponse,
} from '../../client/api-client';
import '../../elements/langhout-checkbox/langhout-checkbox';

import { LanghoutCheckbox } from '../../elements/langhout-checkbox/langhout-checkbox';
import { showNotification } from '../../LanghoutBetonPortal';
import { closeIcon } from '../../elements/icons/icons';
import { sharedStyles } from '../../elements/shared-styles';

@customElement('langhout-beton-edit-users')
export class EditUsers extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        font-size: 17px;
        color: var(--text-color-main);
        height: calc(100vh - 84px);
        max-height: calc(100vh - 84px);
        overflow-y: auto;
        background-color: white;
        z-index: 100;
        padding: 10px;
        padding-left: 30px;
        padding-right: 30px;
      }

      :host([disableButtons]) {
        pointer-events: none;
        opacity: 0.7;
      }

      .row {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-grow: 1;
        flex-wrap: wrap;
        flex-flow: row wrap;
      }

      .centered {
        justify-content: center;
      }

      .inner-wrapper {
        max-width: 900px;
        flex-grow: 1;
      }

      .wrapper {
        padding-bottom: 400px;
        display: flex;
        flex-direction: row;
        justify-content: center;
      }

      .close-wrapper {
        padding: 5px;
      }

      .label {
        text-align: left;
        min-width: 180px;
        max-width: 180px;
        font-weight: bold;
        flex-grow: 1;
      }

      .value {
        text-align: right;
        min-width: 100px;
        flex-grow: 1;
        max-width: 300px;
      }

      langhout-button {
        max-width: 200px;
        min-width: 200px;
        margin-right: 20px;
        margin-top: 20px;
      }

      .button-row {
        max-width: 690px;
      }

      .action-row {
        margin-bottom: 15px;
      }

      .checkbox-wrapper {
        padding-right: 14px;
        min-width: 250px;
      }
    `,
  ];

  @property({ type: Boolean, reflect: true })
  private disableButtons = false;

  @property({ type: Object, reflect: true })
  public currentEditUser: ListUser = <ListUser>{};

  private currentID = '';

  @query('#firstName')
  public firstName?: HTMLInputElement;

  @query('#lastName')
  public lastName?: HTMLInputElement;

  @query('#email')
  public email?: HTMLInputElement;

  @query('#blocked')
  public isBlocked?: LanghoutCheckbox;

  constructor() {
    super();
    this.currentID = getSearchParam(getQueryParamURL(), 'id');
    this.getUser();
  }

  render() {
    return html`
      <div class="row">
        <div class="fill"></div>
        <div
          class="close-wrapper"
          @click="${() => {
            window.history.back();
          }}"
        >
          ${closeIcon()}
        </div>
      </div>
      <div class="wrapper">
        <div class="inner-wrapper">
          <div class="row centered">
            <h2>Gebruiker</h2>
          </div>
          <div class="row centered action-row">
            <div class="label">Voornaam:</div>
            <div class="prod-name">
              <input
                value=${this.currentEditUser.first_name}
                type="text"
                id="firstName"
                name="firstName"
              />
            </div>
          </div>
          <div class="row centered action-row">
            <div class="label">Achternaam:</div>
            <div class="prod-name">
              <input
                value=${this.currentEditUser.last_name}
                type="text"
                id="lastName"
                name="lastName"
              />
            </div>
          </div>
          <div class="row centered action-row">
            <div class="label">Email:</div>
            <div class="prod-name">
              <input
                value=${this.currentEditUser.email}
                type="text"
                id="email"
                name="email"
              />
            </div>
          </div>
          <div class="row centered action-row">
            <div class="label">Geblokkeerd:</div>
            <div class="prod-name checkbox-wrapper">
              <langhout-checkbox
                id="blocked"
                .checked=${this.currentEditUser.blocked}
              ></langhout-checkbox>
            </div>
          </div>
          <div class="row button-row">
            <div class="fill"></div>
            <langhout-button
              text="Gebruiker aanpassen"
              id="editButton"
              @click="${async () => {
                this.disableButtons = true;
                await this.updateUser();
                this.disableButtons = false;
              }}"
            ></langhout-button>
          </div>
        </div>
      </div>
    `;
  }

  updateUser = async () => {
    let firstName = '';
    if (this.firstName) {
      firstName = this.firstName.value;
    }
    if (firstName === '') {
      showNotification("First name can't be empty");
      return;
    }
    let lastName = '';
    if (this.lastName) {
      lastName = this.lastName.value;
    }
    if (firstName === '') {
      showNotification("Last name can't be empty");
      return;
    }
    let email = '';
    if (this.email) {
      email = this.email.value;
    }
    if (email === '') {
      showNotification("Email can't be empty");
      return;
    }
    let isBlocked = false;
    if (this.isBlocked) {
      isBlocked = this.isBlocked.checked;
    }

    const result = await authenticatedAPICall(
      `/users/${this.currentID}`,
      'PUT',
      new Headers(),
      JSON.stringify({
        first_name: firstName,
        last_name: lastName,
        email: email,
        blocked: isBlocked,
      }),
    );
    if (
      result.statusCode !== undefined &&
      result.statusCode >= 200 &&
      result.statusCode < 300
    ) {
      showNotification('User successfully adjusted');
    }
  };

  getUser = async () => {
    if (this.currentID === '') {
      return;
    }
    const result = await authenticatedAPICall(
      `/users/${this.currentID}/edit`,
      'GET',
      new Headers(),
    );
    if (isSuccessfulAPIResponse(result)) {
      this.currentEditUser = result.body;
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-edit-users': EditUsers;
  }
}
